import type { LookupOption, LookupPayload } from "../services/api";
import { resolveLoginUser } from "../services/api";

const OPTION_LOCK_TRUTHY = new Set(["true", "1", "yes", "locked", "t"]);

export const DEFAULT_OPTION_LOCK_MESSAGE =
  "This option is locked and cannot be selected.";

export const CUSTOMER_SUMMARY_LOCK_MESSAGE =
  "Customer Locked: Pending dues have exceeded the grace period. Please unlock the customer from the Customer Module to continue the inspection.";

export function isLockStatusField(
  field: { Json_Attribute?: string; BindValue?: string } | null | undefined
): boolean {
  const key = String(field?.Json_Attribute || field?.BindValue || "")
    .trim()
    .toLowerCase();
  return key === "lock_status";
}

/** True when any summary field is lock_status and that row is locked. */
export function isSummaryRowLocked(
  row: Record<string, unknown> | null | undefined,
  fields: Array<{ Json_Attribute?: string; BindValue?: string } | null | undefined>
): boolean {
  if (!row || !fields?.length) return false;
  return fields.some((field) => {
    if (!isLockStatusField(field)) return false;
    const lockKey = String(
      field?.Json_Attribute || field?.BindValue || "lock_status"
    );
    return isOptionLockedByField(row, lockKey);
  });
}

/** Read optionLock from control config (camelCase or PascalCase). */
export function getOptionLockKey(
  config: Record<string, unknown> | null | undefined
): string | undefined {
  if (!config) return undefined;
  const key = config.optionLock ?? config.OptionLock;
  if (key === undefined || key === null || key === "") return undefined;
  return String(key);
}

/** Read optionLockMessage from control config (camelCase or PascalCase). */
export function getOptionLockMessage(
  config: Record<string, unknown> | null | undefined
): string | undefined {
  if (!config) return undefined;
  const message = config.optionLockMessage ?? config.OptionLockMessage;
  if (message === undefined || message === null) return undefined;
  const text = String(message).trim();
  return text === "" ? undefined : text;
}

/** True when the lookup row's lock field indicates the option cannot be selected. */
export function isOptionLockedByField(
  option: Record<string, unknown>,
  lockField: string
): boolean {
  const value = option[lockField];
  if (value === true || value === 1) return true;
  if (typeof value === "string") {
    return OPTION_LOCK_TRUTHY.has(value.trim().toLowerCase());
  }
  return false;
}

/** Mark options disabled when the field named by optionLock is truthy. */
export function applyOptionLock<T extends LookupOption>(
  options: T[],
  optionLockKey: string | undefined
): T[] {
  if (!optionLockKey || options.length === 0) return options;
  return options.map((opt) => ({
    ...opt,
    disabled: Boolean(opt.disabled) || isOptionLockedByField(opt as any, optionLockKey),
  }));
}

/** Config shape that may carry accountMasterKey (camelCase or PascalCase). */
export type AccountMasterKeyConfig = {
  accountMasterKey?: string;
  AccountMasterKey?: unknown;
};

/** Read accountMasterKey from screen/control settings (supports PascalCase). */
export function getAccountMasterKeyFromConfig(
  config: AccountMasterKeyConfig | Record<string, unknown> | undefined | null
): string | undefined {
  if (!config) return undefined;
  const c = config as AccountMasterKeyConfig;
  const key = c.accountMasterKey ?? c.AccountMasterKey;
  if (key === undefined || key === null || key === "") return undefined;
  return String(key);
}

/** Redux cache key for a lookup (parent + optional accountMasterKey). */
export function getLookupCacheKey(
  lookupName: string,
  parentid?: string | number | null,
  accountMasterKey?: string
): string {
  const hasParent =
    parentid !== undefined && parentid !== null && String(parentid) !== "";
  let key = hasParent ? `${lookupName}_${parentid}` : lookupName;
  if (accountMasterKey) {
    key = `${key}__am__${accountMasterKey}`;
  }
  return key;
}

/** Redux lookup key for a field control (lookup name + optional parent + accountMasterKey). */
export function resolveFieldLookupKey(
  lookupName: string,
  control: AccountMasterKeyConfig | Record<string, unknown> | null | undefined,
  parentValue?: string | number | null
): string {
  const hasParent =
    parentValue !== undefined &&
    parentValue !== null &&
    String(parentValue) !== "";
  return getLookupCacheKey(
    lookupName,
    hasParent ? parentValue : undefined,
    getAccountMasterKeyFromConfig(control)
  );
}

/** Resolve cache key from control/field config and form data. */
export function resolveControlLookupKey(
  lookupName: string,
  control: Record<string, unknown> | undefined | null,
  formData: Record<string, unknown>,
  fallbackParentField?: string
): string {
  const parentField =
    (control?.Parent_Field as string | undefined) ||
    (control?.parentField as string | undefined) ||
    fallbackParentField;
  let parentid: string | number | undefined;
  if (parentField) {
    const pv = formData[parentField];
    if (pv !== undefined && pv !== null && pv !== "") {
      parentid = pv as string | number;
    }
  }
  return getLookupCacheKey(
    lookupName,
    parentid,
    getAccountMasterKeyFromConfig(control)
  );
}

/** Build POST body for /pabws/utility/{lookupName}. */
export function buildLookupPayload(options: {
  parentid?: string | number | null;
  lookuptype?: string;
  accountMasterKey?: string;
  search_arg?: string;
  selectedid?: string | number | null;
  additionalSelectedId?: string | number | null;
  limit?: number;
  skip?: number;
}): LookupPayload {
  const payload: LookupPayload = {
    parentid:
      options.parentid !== undefined && options.parentid !== null
        ? String(options.parentid)
        : "",
    lookuptype: options.lookuptype ?? "Select",
  };
  if (options.accountMasterKey) {
    payload.accountMasterKey = options.accountMasterKey;
  }
  if (options.search_arg !== undefined) {
    payload.search_arg = String(options.search_arg);
  }
  // Include selectedid when provided (including "" for add/prepopulate payloads).
  if (options.selectedid !== undefined && options.selectedid !== null) {
    payload.selectedid = String(options.selectedid).trim();
  }
  const additionalSelectedId =
    options.additionalSelectedId !== undefined &&
    options.additionalSelectedId !== null
      ? String(options.additionalSelectedId).trim()
      : "";
  // When both ids are the same, selectedid alone is enough — discard the duplicate.
  if (
    additionalSelectedId !== "" &&
    additionalSelectedId !== (payload.selectedid ?? "")
  ) {
    payload.additionalSelectedId = additionalSelectedId;
  }
  if (options.limit !== undefined) payload.limit = options.limit;
  if (options.skip !== undefined) payload.skip = options.skip;

  // Always attach branch_id so lookup filters can use <branch_id>.
  const loginUser = resolveLoginUser();
  const branchId = String(loginUser?.branch_id ?? "").trim();
  if (branchId) {
    payload.branch_id = branchId;
  }

  return payload;
}
